import { LitElement, css, html } from 'lit';
import { sharedStyles } from '../styles/shared.js';

/**
 * Kolejka powiadomień. Jedna instancja w powłoce aplikacji — komponenty
 * nie tworzą własnych, tylko wołają `notify()` z core/ui.js.
 *
 * Sam komponent jest czysty: kolejkę dostaje właściwością, a o zamknięciu
 * powiadomienia melduje zdarzeniem. Store interfejsu pilnuje `qz-app`.
 *
 * @prop {Array<{ id: number, text: string, kind: 'info'|'success'|'error',
 *   leaving?: boolean }>} notifications — `leaving` włącza wygaszenie wpisu,
 *   zanim zniknie z kolejki
 * @fires qz-dismiss — zamknięto powiadomienie: `{ detail: { id } }`
 */
export class QzToast extends LitElement {
  static properties = {
    notifications: { type: Array },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        position: fixed;
        right: var(--qz-space-4);
        /* Zapas na pasek zakładek, gdy nawigacja siedzi na dole ekranu —
           powiadomienie ma go nie przykrywać. Wartość podaje powłoka. */
        bottom: calc(var(--qz-space-4) + var(--qz-bottom-inset, 0px));
        z-index: 100;
        display: flex;
        flex-direction: column;
        gap: var(--qz-space-2);
        max-width: min(24rem, calc(100vw - 2 * var(--qz-space-4)));
      }

      .toast {
        display: flex;
        align-items: flex-start;
        gap: var(--qz-space-3);
        padding: var(--qz-space-3) var(--qz-space-4);
        background: var(--qz-surface);
        border: 1px solid var(--qz-line);
        /* Pasek po lewej niesie znaczenie — kolor sam by nie wystarczył. */
        border-left: 3px solid var(--qz-line-strong);
        border-radius: var(--qz-radius-sm);
        box-shadow: var(--qz-shadow);
        font-size: var(--qz-text-sm);

        /*
         * Wejście i wyjście jednym mechanizmem. Reguła allow-discrete
         * przeprowadza display przez przejście, a starting-style niżej podaje
         * stan sprzed wejścia — bez tego powiadomienie znikało w jednej
         * klatce i wyglądało to jak błąd renderowania, a nie jak zamknięcie.
         */
        transition:
          opacity var(--qz-motion-enter) var(--qz-ease),
          transform var(--qz-motion-enter) var(--qz-ease),
          display var(--qz-motion-enter) allow-discrete;
      }

      @starting-style {
        .toast {
          opacity: 0;
          transform: translateY(6px);
        }
      }

      /* Stan wyjściowy: Lit zdejmuje element z drzewa, więc klasę gasnącą
         nakłada render, zanim powiadomienie zniknie z kolejki. */
      .toast.leaving {
        opacity: 0;
        transform: translateY(6px);
      }

      .toast.success {
        border-left-color: var(--qz-success);
      }

      .toast.error {
        border-left-color: var(--qz-danger);
      }

      p {
        margin: 0;
        flex: 1;
      }

      button {
        flex-shrink: 0;
        padding: 0 var(--qz-space-1);
        background: none;
        border: none;
        color: var(--qz-ink-muted);
        font-size: var(--qz-text-lg);
        line-height: 1;
        cursor: pointer;
      }

      button:hover {
        color: var(--qz-ink);
      }
    `,
  ];

  constructor() {
    super();
    this.notifications = [];
  }

  render() {
    const { notifications } = this;

    return html`
      <div role="status" aria-live="polite">
        ${notifications.map(
          (n) => html`
            <div class="toast ${n.kind} ${n.leaving ? 'leaving' : ''}">
              <p>${n.text}</p>
              <button
                type="button"
                aria-label="Zamknij powiadomienie"
                @click=${() => this.#dismiss(n.id)}
              >
                ×
              </button>
            </div>
          `,
        )}
      </div>
    `;
  }

  #dismiss(id) {
    this.dispatchEvent(
      new CustomEvent('qz-dismiss', { detail: { id }, bubbles: true, composed: true }),
    );
  }
}

customElements.define('qz-toast', QzToast);
